import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Auth, Hub } from "aws-amplify";

interface AuthUser {
  username: string;
  email: string;
  sub: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  forgotPasswordSubmit: (email: string, code: string, newPassword: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const mapUser = (cognitoUser: any): AuthUser => ({
  username: cognitoUser.username,
  email: cognitoUser.attributes?.email ?? cognitoUser.username,
  sub: cognitoUser.attributes?.sub ?? cognitoUser.username,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const current = await Auth.currentAuthenticatedUser();
      setUser(mapUser(current));
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signIn" || payload.event === "signUp" || payload.event === "autoSignIn") {
        refreshUser();
      } else if (payload.event === "signOut") {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, [refreshUser]);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    loading,
    signUp: async (email, password) => {
      await Auth.signUp({
        username: email,
        password,
        attributes: { email },
      });
    },
    confirmSignUp: async (email, code) => {
      await Auth.confirmSignUp(email, code);
    },
    resendConfirmationCode: async (email) => {
      await Auth.resendSignUp(email);
    },
    signIn: async (email, password) => {
      await Auth.signIn(email, password);
      await refreshUser();
    },
    signOut: async () => {
      await Auth.signOut();
      setUser(null);
    },
    forgotPassword: async (email) => {
      await Auth.forgotPassword(email);
    },
    forgotPasswordSubmit: async (email, code, newPassword) => {
      await Auth.forgotPasswordSubmit(email, code, newPassword);
    },
    signInWithGoogle: async () => {
      // Wired up in Pass 2 once Google Cloud OAuth is configured.
      await Auth.federatedSignIn({ provider: "Google" as any });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
